import {DestroyRef, Injectable} from '@angular/core';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {TenureService} from '../../../../../plan-information/tenure/tenure.service';
import {NgEventBus} from 'ng-event-bus';
import {MapModeService} from '../../../../services/map-mode.service';
import {SpatialSequenceService} from '../../../../../common/service/spatial-sequence.service';
import {PrintAreaService} from '../../../../../common/service/print-area.service';
import {AppConfigService} from '../../../../../common/service/app-config.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {dmsToDd} from '../../../../services/features/angle/angle-tools';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Injectable({
    providedIn: 'root'
})
export class AnnotationConstructionToolService {
    private readonly title = 'AnnotationConstructionToolService';

    private getAttributeMaps: () => {attributes: any; attributesGroup: any};

    private attributesGroup = {};

    private mapClickHandler: any;

    private tool: any;

    private attributes: any;

    constructor(
        private appConfigService: AppConfigService,
        private destroyRef: DestroyRef,
        private esriModuleService: EsriModuleService,
        private mapModeService: MapModeService,
        private eventBus: NgEventBus,
        private printAreaService: PrintAreaService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService,
        private tenureService: TenureService
    ) {
        this.eventBus
            .on('constructFinishAnnotationTool')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                console.debug(`${this.title} - on - constructFinishAnnotationTool`);
                this.removeMapClickHandler();
            });
    }

    setupFeatureEventModification(getAttributeMaps: () => any, tool: any, attributes: any) {
        console.debug(`${this.title} - setupFeatureEventModification`);

        this.getAttributeMaps = getAttributeMaps;
        this.tool = tool;
        this.attributes = attributes;
    }

    create(getDetails: () => {attributes: any; attributesGroup: any}) {
        console.debug(`${this.title} - create`);

        const cursorUrl =
            this.mapModeService.viewMode === this.appConfigService.getDesignProperties().VIEW_MODES.BLACK_BACKGROUND_MODE
                ? this.spatialUtilService.getBlackModeCrossCursorUrl()
                : this.spatialUtilService.getWhiteModeCrossCursorUrl();
        const map = this.spatialUtilService.getMap();
        map.setMapCursor(cursorUrl);

        const details = getDetails();
        this.attributes = details.attributes;
        this.attributesGroup = details.attributesGroup;

        this.removeMapClickHandler();

        this.mapClickHandler = map.on('click', (evt: {mapPoint: any}) => {
            const details = getDetails();
            this.attributes = details.attributes;
            this.attributesGroup = details.attributesGroup;
            this.constructAnnotation(this.attributes, evt.mapPoint);
        });
    }

    private constructAnnotation(attributes: any, mapPoint: any) {
        console.debug(`${this.title} - createAnnotation`);

        if ([2, 24, 25].includes(attributes.featuresubtype)) {
            //this category can be added to lookup to deal with this annotation merging

            const count = attributes.attribute1.split('[').length;

            let attribute1 = attributes.attribute1;
            attribute1 = attribute1.replace(/\[(.+?)]/, attributes.attribute2);

            attribute1 = attribute1.replace(/( METRES )/, '$1 \n');

            if (count > 2) {
                if (attributes.featuresubtype === 24) {
                    attributes.attribute3 = this.tenureService.sanitiseRangesText(attributes.attribute3);
                }
                attribute1 = attribute1.replace(/\[(.+?)]/, attributes.attribute3);
            }

            attributes['attributeCustom'] = attribute1;
        }

        const enlargement = this.spatialUtilService.getEnlargements().find((f: any) => f.attributes.id === this.mapModeService.mapMode.id);

        const planScale =
            enlargement && this.mapModeService.mapMode.type === 'enlargement'
                ? enlargement.attributes.scale
                : this.printAreaService.getReferenceScaleDivisor();

        const baseOffset = planScale * 0.004;

        const annotationsToBeCreated = [];

        const sequence = this.spatialSequenceService.getAnnotationSequence(1);

        let isGroup = !Object.values(this.attributesGroup).some(v => v === false);

        for (const [groupKey, groupValue] of Object.entries(this.attributesGroup)) {
            if (groupValue === false || isGroup) {
                const attributeNumber = groupKey.replace(/^\D+/g, '');
                const rotation = ((attributes.rotation - 90) * Math.PI) / 180;
                const baseOffsetX = baseOffset * Math.sin(rotation);
                const baseOffsetY = baseOffset * Math.cos(rotation);

                const offsetX = isGroup ? 0 : baseOffsetX * (attributeNumber === '' ? 0 : parseInt(attributeNumber) - 1);
                const offsetY = isGroup ? 0 : baseOffsetY * (attributeNumber === '' ? 0 : parseInt(attributeNumber) - 1);

                const pointGeometry = new this.esriModuleService.geometry.Point(mapPoint.x - offsetX, mapPoint.y - offsetY);
                pointGeometry.setSpatialReference(mapPoint.spatialReference);

                const pointAttributes: {x: number; y: number; rotation?: number; rotationdms?: string} = {x: mapPoint.x, y: mapPoint.y};
                let validFeature = !isGroup;

                for (const [attributeKey, attribute] of Object.entries(attributes) as any[]) {
                    if (attribute && typeof attribute === 'object') {
                        attribute.id = sequence[0];
                        pointAttributes[attributeKey] = attribute.id;
                    } else {
                        if (
                            this.attributesGroup[attributeKey] ||
                            attributeKey == groupKey ||
                            typeof this.attributesGroup[attributeKey] === 'undefined'
                        ) {
                            pointAttributes[attributeKey] = attribute;

                            if (isGroup && attributeKey.includes('attribute')) {
                                validFeature = validFeature || !!attribute;
                            } else {
                                validFeature = validFeature && !!attribute;
                            }
                        }
                    }
                }

                if (this.mapModeService.isMapModeType('enlargement')) {
                    pointAttributes['enlargementid'] = this.mapModeService.mapMode.id;
                }

                const pointGraphic = new this.esriModuleService.Graphic(pointGeometry);
                if (pointAttributes.rotationdms) {
                    pointAttributes.rotation = dmsToDd(pointAttributes.rotationdms);
                }
                pointGraphic.attributes = pointAttributes;

                if (validFeature) {
                    annotationsToBeCreated.push(pointGraphic);
                }

                isGroup = false;
            }
        }

        this.eventBus.cast('createAnnotations', annotationsToBeCreated);
    }

    private removeMapClickHandler() {
        if (this.mapClickHandler) {
            this.mapClickHandler.remove();
        }
    }
}
